import React, {useEffect, useRef, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {Button, Tooltip} from '@mui/material'
import {
    ArrowRight,
    Building2,
    CheckCircle2,
    ChevronDown,
    Clock,
    CreditCard,
    Globe,
    Search,
    ShieldCheck,
    Zap
} from 'lucide-react'
import ReactCountryFlag from 'react-country-flag'
import {toast} from 'react-toastify'
import api from '../../../api/axios'
import Lottie from "lottie-react"
import successAnimation from '../../../assets/lottie/success.json'
import {useSelector} from 'react-redux'
import {RootState} from "../../../store";

type CountryInfo = {
    name: string
    code: string
    supportsBank: boolean
    supportsCrypto: boolean
    supportsDebit?: boolean
}

const SUPPORTED_COUNTRIES: CountryInfo[] = [
    {name: 'Albania', code: 'al', supportsBank: true, supportsCrypto: true},
    {name: 'Algeria', code: 'dz', supportsBank: true, supportsCrypto: false},
    {name: 'Angola', code: 'ao', supportsBank: true, supportsCrypto: false},
    {name: 'Antigua & Barbuda', code: 'ag', supportsBank: true, supportsCrypto: true},
    {name: 'Argentina', code: 'ar', supportsBank: true, supportsCrypto: true},
    {name: 'Armenia', code: 'am', supportsBank: true, supportsCrypto: true},
    {name: 'Australia', code: 'au', supportsBank: true, supportsCrypto: true},
    {name: 'Austria', code: 'at', supportsBank: true, supportsCrypto: true},
    {name: 'Azerbaijan', code: 'az', supportsBank: true, supportsCrypto: true},
    {name: 'Bahamas', code: 'bs', supportsBank: true, supportsCrypto: true},
    {name: 'Bahrain', code: 'bh', supportsBank: true, supportsCrypto: true},
    {name: 'Bangladesh', code: 'bd', supportsBank: true, supportsCrypto: false},
    {name: 'Belgium', code: 'be', supportsBank: true, supportsCrypto: true},
    {name: 'Benin', code: 'bj', supportsBank: true, supportsCrypto: true},
    {name: 'Bhutan', code: 'bt', supportsBank: true, supportsCrypto: false},
    {name: 'Bolivia', code: 'bo', supportsBank: true, supportsCrypto: true},
    {name: 'Bosnia & Herzegovina', code: 'ba', supportsBank: true, supportsCrypto: true},
    {name: 'Botswana', code: 'bw', supportsBank: true, supportsCrypto: true},
    {name: 'Brazil', code: 'br', supportsBank: true, supportsCrypto: true},
    {name: 'Brunei', code: 'bn', supportsBank: true, supportsCrypto: true},
    {name: 'Bulgaria', code: 'bg', supportsBank: true, supportsCrypto: true},
    {name: 'Cambodia', code: 'kh', supportsBank: true, supportsCrypto: false},
    {name: 'Canada', code: 'ca', supportsBank: true, supportsCrypto: true},
    {name: 'Chile', code: 'cl', supportsBank: true, supportsCrypto: true},
    {name: 'Colombia', code: 'co', supportsBank: true, supportsCrypto: true},
    {name: 'Costa Rica', code: 'cr', supportsBank: true, supportsCrypto: true},
    {name: 'Croatia', code: 'hr', supportsBank: true, supportsCrypto: true},
    {name: 'Cyprus', code: 'cy', supportsBank: true, supportsCrypto: true},
    {name: 'Czech Republic', code: 'cz', supportsBank: true, supportsCrypto: true},
    {name: "Côte d'Ivoire", code: 'ci', supportsBank: true, supportsCrypto: false},
    {name: 'Denmark', code: 'dk', supportsBank: true, supportsCrypto: true},
    {name: 'Dominican Republic', code: 'do', supportsBank: true, supportsCrypto: true},
    {name: 'Ecuador', code: 'ec', supportsBank: true, supportsCrypto: true},
    {name: 'Egypt', code: 'eg', supportsBank: true, supportsCrypto: false},
    {name: 'El Salvador', code: 'sv', supportsBank: true, supportsCrypto: true},
    {name: 'Estonia', code: 'ee', supportsBank: true, supportsCrypto: true},
    {name: 'Ethiopia', code: 'et', supportsBank: true, supportsCrypto: false},
    {name: 'Finland', code: 'fi', supportsBank: true, supportsCrypto: true},
    {name: 'France', code: 'fr', supportsBank: true, supportsCrypto: true},
    {name: 'Gabon', code: 'ga', supportsBank: true, supportsCrypto: false},
    {name: 'Gambia', code: 'gm', supportsBank: true, supportsCrypto: false},
    {name: 'Germany', code: 'de', supportsBank: true, supportsCrypto: true},
    {name: 'Ghana', code: 'gh', supportsBank: true, supportsCrypto: true},
    {name: 'Greece', code: 'gr', supportsBank: true, supportsCrypto: true},
    {name: 'Guatemala', code: 'gt', supportsBank: true, supportsCrypto: true},
    {name: 'Guyana', code: 'gy', supportsBank: true, supportsCrypto: true},
    {name: 'Hong Kong SAR China', code: 'hk', supportsBank: true, supportsCrypto: true},
    {name: 'Hungary', code: 'hu', supportsBank: true, supportsCrypto: true},
    {name: 'Iceland', code: 'is', supportsBank: true, supportsCrypto: true},
    {name: 'India', code: 'in', supportsBank: true, supportsCrypto: true},
    {name: 'Indonesia', code: 'id', supportsBank: true, supportsCrypto: true},
    {name: 'Ireland', code: 'ie', supportsBank: true, supportsCrypto: true},
    {name: 'Israel', code: 'il', supportsBank: true, supportsCrypto: true},
    {name: 'Italy', code: 'it', supportsBank: true, supportsCrypto: true},
    {name: 'Jamaica', code: 'jm', supportsBank: true, supportsCrypto: true},
    {name: 'Japan', code: 'jp', supportsBank: true, supportsCrypto: true},
    {name: 'Jordan', code: 'jo', supportsBank: true, supportsCrypto: true},
    {name: 'Kazakhstan', code: 'kz', supportsBank: true, supportsCrypto: true},
    {name: 'Kenya', code: 'ke', supportsBank: true, supportsCrypto: true},
    {name: 'Kuwait', code: 'kw', supportsBank: true, supportsCrypto: true},
    {name: 'Latvia', code: 'lv', supportsBank: true, supportsCrypto: true},
    {name: 'Liechtenstein', code: 'li', supportsBank: true, supportsCrypto: true},
    {name: 'Lithuania', code: 'lt', supportsBank: true, supportsCrypto: true},
    {name: 'Luxembourg', code: 'lu', supportsBank: true, supportsCrypto: true},
    {name: 'Macao SAR China', code: 'mo', supportsBank: true, supportsCrypto: true},
    {name: 'Madagascar', code: 'mg', supportsBank: true, supportsCrypto: true},
    {name: 'Malaysia', code: 'my', supportsBank: true, supportsCrypto: true},
    {name: 'Malta', code: 'mt', supportsBank: true, supportsCrypto: true},
    {name: 'Mauritius', code: 'mu', supportsBank: true, supportsCrypto: true},
    {name: 'Mexico', code: 'mx', supportsBank: true, supportsCrypto: true},
    {name: 'Moldova', code: 'md', supportsBank: true, supportsCrypto: true},
    {name: 'Monaco', code: 'mc', supportsBank: true, supportsCrypto: true},
    {name: 'Mongolia', code: 'mn', supportsBank: true, supportsCrypto: true},
    {name: 'Morocco', code: 'ma', supportsBank: true, supportsCrypto: false},
    {name: 'Mozambique', code: 'mz', supportsBank: true, supportsCrypto: true},
    {name: 'Namibia', code: 'na', supportsBank: true, supportsCrypto: true},
    {name: 'Netherlands', code: 'nl', supportsBank: true, supportsCrypto: true},
    {name: 'New Zealand', code: 'nz', supportsBank: true, supportsCrypto: true},
    {name: 'Niger', code: 'ne', supportsBank: true, supportsCrypto: true},
    {name: 'Nigeria', code: 'ng', supportsBank: true, supportsCrypto: true},
    {name: 'North Macedonia', code: 'mk', supportsBank: true, supportsCrypto: true},
    {name: 'Norway', code: 'no', supportsBank: true, supportsCrypto: true},
    {name: 'Oman', code: 'om', supportsBank: true, supportsCrypto: true},
    {name: 'Pakistan', code: 'pk', supportsBank: true, supportsCrypto: true},
    {name: 'Panama', code: 'pa', supportsBank: true, supportsCrypto: true},
    {name: 'Paraguay', code: 'py', supportsBank: true, supportsCrypto: true},
    {name: 'Peru', code: 'pe', supportsBank: true, supportsCrypto: true},
    {name: 'Philippines', code: 'ph', supportsBank: true, supportsCrypto: true},
    {name: 'Poland', code: 'pl', supportsBank: true, supportsCrypto: true},
    {name: 'Portugal', code: 'pt', supportsBank: true, supportsCrypto: true},
    {name: 'Qatar', code: 'qa', supportsBank: true, supportsCrypto: true},
    {name: 'Romania', code: 'ro', supportsBank: true, supportsCrypto: true},
    {name: 'Rwanda', code: 'rw', supportsBank: true, supportsCrypto: true},
    {name: 'Samoa', code: 'ws', supportsBank: true, supportsCrypto: true},
    {name: 'San Marino', code: 'sm', supportsBank: true, supportsCrypto: true},
    {name: 'Saudi Arabia', code: 'sa', supportsBank: true, supportsCrypto: true},
    {name: 'Senegal', code: 'sn', supportsBank: true, supportsCrypto: true},
    {name: 'Serbia', code: 'rs', supportsBank: true, supportsCrypto: true},
    {name: 'Singapore', code: 'sg', supportsBank: true, supportsCrypto: true},
    {name: 'Slovakia', code: 'sk', supportsBank: true, supportsCrypto: true},
    {name: 'Slovenia', code: 'si', supportsBank: true, supportsCrypto: true},
    {name: 'South Africa', code: 'za', supportsBank: true, supportsCrypto: true},
    {name: 'South Korea', code: 'kr', supportsBank: true, supportsCrypto: true},
    {name: 'Spain', code: 'es', supportsBank: true, supportsCrypto: true},
    {name: 'Sri Lanka', code: 'lk', supportsBank: true, supportsCrypto: true},
    {name: 'St. Lucia', code: 'lc', supportsBank: true, supportsCrypto: true},
    {name: 'St. Vincent & Grenadines', code: 'vc', supportsBank: true, supportsCrypto: true},
    {name: 'Sweden', code: 'se', supportsBank: true, supportsCrypto: true},
    {name: 'Switzerland', code: 'ch', supportsBank: true, supportsCrypto: true},
    {name: 'Taiwan', code: 'tw', supportsBank: true, supportsCrypto: true},
    {name: 'Tanzania', code: 'tz', supportsBank: true, supportsCrypto: true},
    {name: 'Thailand', code: 'th', supportsBank: true, supportsCrypto: true},
    {name: 'Trinidad & Tobago', code: 'tt', supportsBank: true, supportsCrypto: true},
    {name: 'Tunisia', code: 'tn', supportsBank: true, supportsCrypto: true},
    {name: 'Turkey', code: 'tr', supportsBank: true, supportsCrypto: true},
    {name: 'United Arab Emirates', code: 'ae', supportsBank: true, supportsCrypto: true},
    {name: 'United Kingdom', code: 'gb', supportsBank: true, supportsCrypto: true},
    {name: 'United States', code: 'us', supportsBank: true, supportsCrypto: true, supportsDebit: true},
    {name: 'Uruguay', code: 'uy', supportsBank: true, supportsCrypto: true},
    {name: 'Uzbekistan', code: 'uz', supportsBank: true, supportsCrypto: true},
    {name: 'Vietnam', code: 'vn', supportsBank: true, supportsCrypto: false},
]

const StripeOnboarding = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    // Redux selectors
    const stripe = useSelector((state: RootState) => state.auth.paymentAccounts?.stripe)
    const stripeConnectEnabled = stripe?.stripeConnectEnabled ?? false

    const stripeParam = searchParams.get('stripe')

    const [dropdownSearch, setDropdownSearch] = useState('')
    const [overviewSearch, setOverviewSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [selectedCountry, setSelectedCountry] = useState<CountryInfo | null>(null)
    const [detectingLocation, setDetectingLocation] = useState<boolean>(true)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Handle failure query parameters on mount and trigger error toast notification
    useEffect(() => {
        if (stripeParam === 'failed' || searchParams.get('error') || searchParams.get('refresh')) {
            toast.error('Connect onboarding failed or was cancelled, please try again.')
            // Remove query params so refreshing doesn't re-trigger the toast
            setSearchParams({})
        }
    }, [stripeParam, searchParams, setSearchParams])

    // Fetch user country via IP on component mount to set default selection
    useEffect(() => {
        const fetchUserCountry = async () => {
            try {
                const response = await fetch('https://ipapi.co/json/')
                const data = await response.json()
                if (data && data.country_code) {
                    const code = data.country_code.toLowerCase()
                    const found = SUPPORTED_COUNTRIES.find(c => c.code === code)
                    if (found) {
                        setSelectedCountry(found)
                    } else {
                        setSelectedCountry(SUPPORTED_COUNTRIES.find(c => c.code === 'de') || SUPPORTED_COUNTRIES[0])
                    }
                } else {
                    setSelectedCountry(SUPPORTED_COUNTRIES.find(c => c.code === 'de') || SUPPORTED_COUNTRIES[0])
                }
            } catch (error) {
                console.error('Failed to fetch location by IP', error)
                setSelectedCountry(SUPPORTED_COUNTRIES.find(c => c.code === 'de') || SUPPORTED_COUNTRIES[0])
            } finally {
                setDetectingLocation(false)
            }
        }

        fetchUserCountry()
    }, [])

    // Close custom dropdown when clicking outside of the element container
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Handle onboarding request submission with selected country code
    const handleOnboardClick = async () => {
        if (!selectedCountry) return
        try {
            setLoading(true)
            const countryCode = selectedCountry.code.toUpperCase()
            const response = await api.post<{
                onboardingUrl: string
            }>(`/stripe/connect/onboard?countryCode=${countryCode}`)
            if (response.data.onboardingUrl) {
                window.location.href = response.data.onboardingUrl
            }
        } catch (error) {
            console.error('Failed to initialize Stripe Connect onboarding', error)
            toast.error('Connect onboarding failed, please try again.')
        } finally {
            setLoading(false)
        }
    }

    const dropdownFilteredCountries = SUPPORTED_COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(dropdownSearch.toLowerCase())
    )

    const overviewFilteredCountries = SUPPORTED_COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(overviewSearch.toLowerCase())
    )

    // Determine what to display:
    // 1. If stripeParam === 'success' -> show success view WITH Lottie animation
    // 2. If stripeConnectEnabled is true (and no failure param) -> show success view WITHOUT Lottie animation
    // 3. Otherwise -> show steps 1 and 2
    const isExplicitSuccess = stripeParam === 'success'
    const isAlreadyConnected = stripeConnectEnabled && stripeParam !== 'failed'
    const showSuccessView = isExplicitSuccess || isAlreadyConnected

    return (
        <div
            className="min-h-[calc(100vh-80px)] bg-white dark:bg-neutral-900/40 text-light-text dark:text-dark-text p-4 flex flex-col gap-4 font-sans overflow-y-auto">
            {/* Unified container box for header, features, and steps */}
            <div
                className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700/60 rounded-lg p-4 md:p-5 flex flex-col gap-4 shadow-xl">
                {/* Header content */}
                <div className="flex flex-col gap-1">
                    <div
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-light-primary dark:text-dark-primary">
                        <Zap size={14}/> Stripe Connect Payouts
                    </div>
                    <h1 className="text-xl md:text-2xl font-black tracking-tight text-light-text dark:text-white">
                        Complete Stripe Onboarding
                    </h1>
                    <p className="text-xs text-gray-600 dark:text-neutral-400">
                        Connect your account securely to enable direct bank transfers and global payout options.
                    </p>
                </div>

                {/* Feature highlights grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div
                        className="bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-3 shadow-sm flex items-start gap-3">
                        <Tooltip title="Fast automated identity and account verification" arrow>
                            <div
                                className="w-8 h-8 rounded-md bg-blue-500/10 text-light-primary dark:text-dark-primary flex items-center justify-center shrink-0 cursor-help">
                                <Zap size={16}/>
                            </div>
                        </Tooltip>
                        <div>
                            <div className="font-bold text-xs text-light-text dark:text-white">Fast Onboarding</div>
                            <div className="text-[11px] text-gray-600 dark:text-neutral-400 mt-0.5">Verification under 5
                                minutes.
                            </div>
                        </div>
                    </div>

                    <div
                        className="bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-3 shadow-sm flex items-start gap-3">
                        <Tooltip title="Payout availability across global banking networks" arrow>
                            <div
                                className="w-8 h-8 rounded-md bg-blue-500/10 text-light-primary dark:text-dark-primary flex items-center justify-center shrink-0 cursor-help">
                                <Globe size={16}/>
                            </div>
                        </Tooltip>
                        <div>
                            <div className="font-bold text-xs text-light-text dark:text-white">Global Support</div>
                            <div className="text-[11px] text-gray-600 dark:text-neutral-400 mt-0.5">Local currency
                                payouts.
                            </div>
                        </div>
                    </div>

                    <div
                        className="bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-3 shadow-sm flex items-start gap-3">
                        <Tooltip title="Funds are secured in platform escrow until trade completion" arrow>
                            <div
                                className="w-8 h-8 rounded-md bg-blue-500/10 text-light-primary dark:text-dark-primary flex items-center justify-center shrink-0 cursor-help">
                                <ShieldCheck size={16}/>
                            </div>
                        </Tooltip>
                        <div>
                            <div className="font-bold text-xs text-light-text dark:text-white">Secure Escrow</div>
                            <div className="text-[11px] text-gray-600 dark:text-neutral-400 mt-0.5">Safe platform
                                protection.
                            </div>
                        </div>
                    </div>

                    <div
                        className="bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-3 shadow-sm flex items-start gap-3">
                        <Tooltip title="Reliable payouts transferred within standard business hours" arrow>
                            <div
                                className="w-8 h-8 rounded-md bg-blue-500/10 text-light-primary dark:text-dark-primary flex items-center justify-center shrink-0 cursor-help">
                                <Clock size={16}/>
                            </div>
                        </Tooltip>
                        <div>
                            <div className="font-bold text-xs text-light-text dark:text-white">Quick Settlement</div>
                            <div className="text-[11px] text-gray-600 dark:text-neutral-400 mt-0.5">Standard business
                                timeframes.
                            </div>
                        </div>
                    </div>
                </div>

                {showSuccessView ? (
                    <div
                        className="bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-6 shadow-sm flex flex-col items-center justify-center text-center">
                        {isExplicitSuccess && (
                            <div className="w-40">
                                <Lottie
                                    loop={true}
                                    animationData={successAnimation}
                                    style={{width: '100%', height: '100%'}}
                                />
                            </div>
                        )}
                        <div className="flex flex-col items-center gap-2">
                            <span
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                <CheckCircle2 size={14}/> Stripe Connect Completed
                            </span>
                            <h3 className="text-sm font-black text-light-text dark:text-white">
                                Your account has been successfully connected!
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-neutral-400 max-w-md">
                                You are now ready to receive payouts and manage transactions seamlessly through Stripe
                                Connect.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <div
                            className="flex flex-col gap-2.5 bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-4 shadow-sm">
                            <div className="flex items-center gap-2.5">
                                <span
                                    className="w-5 h-5 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center text-xs font-bold">1</span>
                                <h3 className="text-xs font-black tracking-tight text-light-text dark:text-white">Select
                                    Your Country</h3>
                            </div>
                            <p className="text-[11px] text-gray-600 dark:text-neutral-400">
                                Choose the country where your legal entity or bank account is registered.
                            </p>

                            <div className="relative" ref={dropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="w-full flex items-center justify-between gap-3 bg-neutral-50 dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-md px-3 py-2.5 text-xs font-bold text-light-text dark:text-white focus:outline-none focus:border-blue-500 shadow-sm"
                                >
                                    <div className="flex items-center gap-2.5 truncate">
                                        {detectingLocation ? (
                                            <span
                                                className="text-gray-400 text-xs font-normal">Detecting location...</span>
                                        ) : selectedCountry ? (
                                            <>
                                                <ReactCountryFlag svg countryCode={selectedCountry.code} style={{
                                                    width: '1.2em',
                                                    height: '1.2em',
                                                    flexShrink: 0
                                                }}/>
                                                <span className="truncate">{selectedCountry.name}</span>
                                            </>
                                        ) : (
                                            <span className="text-gray-400 text-xs font-normal">Select country...</span>
                                        )}
                                    </div>
                                    <ChevronDown size={14}
                                                 className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}/>
                                </button>

                                {isDropdownOpen && (
                                    <div
                                        className="absolute left-0 right-0 mt-1 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-xl z-50 overflow-hidden flex flex-col">
                                        <div
                                            className="p-2 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                                            <div className="relative">
                                                <Search size={14}
                                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                                                <input
                                                    type="text"
                                                    placeholder="Search country..."
                                                    value={dropdownSearch}
                                                    onChange={(e) => setDropdownSearch(e.target.value)}
                                                    className="w-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-md pl-8 pr-3 py-1.5 text-xs text-light-text dark:text-white focus:outline-none focus:border-blue-500"
                                                    autoFocus
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-56 overflow-y-auto p-1 flex flex-col gap-1">
                                            {dropdownFilteredCountries.length > 0 ? (
                                                dropdownFilteredCountries.map(country => (
                                                    <div
                                                        key={country.code}
                                                        onClick={() => {
                                                            setSelectedCountry(country)
                                                            setIsDropdownOpen(false)
                                                            setDropdownSearch('')
                                                        }}
                                                        className={`flex items-center justify-between px-3 py-1.5 rounded text-xs cursor-pointer transition-colors ${selectedCountry?.code === country.code ? 'bg-blue-500/10 text-blue-500 font-bold' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-light-text dark:text-neutral-200'}`}
                                                    >
                                                        <div className="flex items-center gap-2 truncate">
                                                            <ReactCountryFlag svg countryCode={country.code} style={{
                                                                width: '1.1em',
                                                                height: '1.1em',
                                                                flexShrink: 0
                                                            }}/>
                                                            <span className="truncate">{country.name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 shrink-0 text-gray-400">
                                                            {country.supportsBank && (
                                                                <Tooltip title="Bank Transfer" arrow>
                                                                    <span><Building2 size={11}
                                                                                     className="text-emerald-500"/></span>
                                                                </Tooltip>
                                                            )}
                                                            {country.supportsCrypto && (
                                                                <Tooltip title="Crypto Payout" arrow>
                                                                    <span
                                                                        className="w-3 h-3 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 text-[8px] font-black">$</span>
                                                                </Tooltip>
                                                            )}
                                                            {country.supportsDebit && (
                                                                <Tooltip title="Debit Card" arrow>
                                                                    <span><CreditCard size={11}
                                                                                      className="text-blue-500"/></span>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-3 text-center text-xs text-gray-500">No country
                                                    found</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {selectedCountry && (
                                <div
                                    className="bg-neutral-50 dark:bg-neutral-900/40 border border-neutral-200 dark:border-neutral-700/60 rounded p-2 flex flex-col gap-1">
                                    <div
                                        className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                        Payout Methods:
                                    </div>
                                    <div
                                        className="flex items-center gap-1.5 flex-wrap text-[11px] text-light-text dark:text-neutral-200">
                                        {selectedCountry.supportsBank && (
                                            <span
                                                className="flex items-center gap-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-500/20">
                                                <Building2 size={11}/> Bank
                                            </span>
                                        )}
                                        {selectedCountry.supportsCrypto && (
                                            <span
                                                className="flex items-center gap-1 bg-orange-500/10 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded border border-orange-500/20">
                                                <span
                                                    className="w-2.5 h-2.5 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 text-[7px] font-black">$</span> Crypto
                                            </span>
                                        )}
                                        {selectedCountry.supportsDebit && (
                                            <span
                                                className="flex items-center gap-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/20">
                                                <CreditCard size={11}/> Debit
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div
                            className="flex flex-col justify-between bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700/60 rounded-md p-4 shadow-sm">
                            <div className="flex flex-col gap-2.5">
                                <div className="flex items-center gap-2.5">
                                    <span
                                        className="w-5 h-5 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center text-xs font-bold">2</span>
                                    <h3 className="text-xs font-black tracking-tight text-light-text dark:text-white">Connect
                                        to Stripe</h3>
                                </div>
                                <p className="text-[11px] text-gray-600 dark:text-neutral-400">
                                    {selectedCountry
                                        ? `Ready to proceed with ${selectedCountry.name}. Click below to open the secure Stripe onboarding workflow.`
                                        : 'Please select a country in Step 1 to enable the connection.'}
                                </p>
                            </div>

                            <div className="mt-3">
                                <div className="relative group w-full">
                                    {selectedCountry && (
                                        <div
                                            className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-700 rounded-lg blur-md opacity-85 group-hover:opacity-100 transition duration-300 animate-pulse"/>
                                    )}
                                    <Button
                                        variant="contained"
                                        disabled={loading || !selectedCountry}
                                        onClick={handleOnboardClick}
                                        className={`!relative !w-full !text-xs !font-semibold !px-4 !py-2.5 !rounded-lg !shadow-lg !normal-case !flex !items-center !justify-center !gap-2 ${selectedCountry ? '!bg-dark-primary !text-white' : '!bg-neutral-300 dark:!bg-neutral-700 !text-neutral-500 dark:!text-neutral-400 !cursor-not-allowed'}`}
                                    >
                                        {loading ? 'Preparing...' : 'Start Stripe Onboarding'}
                                        <ArrowRight size={14}/>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Complete Country Overview Section */}
            <div
                className="p-4 md:p-5 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700/60 shadow-xl">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-3 gap-3">
                    <div>
                        <h3 className="text-xs font-black tracking-tight flex items-center gap-2 text-light-text dark:text-white">
                            <Globe size={15} className="text-light-primary dark:text-dark-primary"/> Complete Country
                            Overview
                        </h3>
                        <p className="text-[11px] text-gray-600 dark:text-neutral-400 mt-0.5">Browse all supported
                            countries and available payout methods.</p>
                    </div>

                    <div className="relative w-full sm:w-56">
                        <Search size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-400"/>
                        <input
                            type="text"
                            placeholder="Filter countries..."
                            value={overviewSearch}
                            onChange={e => setOverviewSearch(e.target.value)}
                            className="w-full bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-700 rounded-md pl-8 pr-3 py-1.5 text-xs text-light-text dark:text-dark-text focus:outline-none focus:border-blue-500"
                        />
                    </div>
                </div>

                <div
                    className="max-h-60 overflow-y-auto pr-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {overviewFilteredCountries.map(country => (
                        <div
                            key={country.code}
                            onClick={() => {
                                setSelectedCountry(country)
                                window.scrollTo({top: 0, behavior: 'smooth'})
                            }}
                            className={`bg-white dark:bg-neutral-900/60 border rounded p-2 flex items-center justify-between shadow-sm cursor-pointer transition-colors ${selectedCountry?.code === country.code ? 'border-blue-500 bg-blue-500/5' : 'border-neutral-200 dark:border-neutral-700/60 hover:border-blue-500'}`}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <ReactCountryFlag svg countryCode={country.code}
                                                  style={{width: '1.1em', height: '1.1em', flexShrink: 0}}/>
                                <span
                                    className="text-[11px] font-bold truncate text-light-text dark:text-neutral-200">{country.name}</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 text-gray-400 dark:text-neutral-400">
                                {country.supportsBank && (
                                    <Tooltip title="Supports direct bank transfer" arrow>
                                        <span className="cursor-help flex items-center">
                                            <Building2 size={11} className="text-emerald-500"/>
                                        </span>
                                    </Tooltip>
                                )}
                                {country.supportsCrypto && (
                                    <Tooltip title="Supports cryptocurrency payouts" arrow>
                                        <span className="cursor-help flex items-center">
                                            <div
                                                className="w-3 h-3 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 text-[8px] font-black">
                                                $
                                            </div>
                                        </span>
                                    </Tooltip>
                                )}
                                {country.supportsDebit && (
                                    <Tooltip title="Supports instant debit card payouts" arrow>
                                        <span className="cursor-help flex items-center">
                                            <CreditCard size={11} className="text-light-primary dark:text-dark-primary"/>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default StripeOnboarding